import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { NetworkType, SupportedNetworks } from 'utils/constants';

const STORAGE_KEY = 'suppressNetworkSwitchWarning';

const readSuppressed = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeSuppressed = (value: boolean) => {
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // Private browsing or quota exceeded — ignore
  }
};

interface UseNetworkSwitchWarningOptions {
  isAuthenticated: boolean;
  updateConnectorNetwork: (network: NetworkType) => void;
}

export const useNetworkSwitchWarning = ({
  isAuthenticated,
  updateConnectorNetwork,
}: UseNetworkSwitchWarningOptions) => {
  const [pendingNetwork, setPendingNetwork] = useState<SupportedNetworks | null>(null);
  const [suppressWarning, setSuppressWarning] = useState(readSuppressed);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleSelection = useCallback(
    (network: SupportedNetworks) => {
      if (isAuthenticated && !suppressWarning) {
        setPendingNetwork(network);
        onOpen();
      } else {
        updateConnectorNetwork(network.toLowerCase() as NetworkType);
      }
    },
    [isAuthenticated, suppressWarning, updateConnectorNetwork, onOpen],
  );

  const confirmSwitch = useCallback(() => {
    if (pendingNetwork) {
      updateConnectorNetwork(pendingNetwork.toLowerCase() as NetworkType);
    }
    onClose();
    setPendingNetwork(null);
  }, [pendingNetwork, updateConnectorNetwork, onClose]);

  const handleSuppressChange = useCallback((checked: boolean) => {
    setSuppressWarning(checked);
    writeSuppressed(checked);
  }, []);

  const WarningModal = useMemo(
    () => (
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pt={6} px={6} pb={0}>
            <Text size="lg" variant="bodyBold">
              Switch Network
            </Text>
            <ModalCloseButton color="#565656" mt={3} mr={2} />
          </ModalHeader>
          <ModalBody px={6} pb={6} pt={4}>
            <Text size="sm" color="blackAlpha.700">
              Switching networks will disconnect your current session. You will need to reconnect
              your wallet on the new network.
            </Text>
            <Checkbox
              mt={4}
              size="sm"
              isChecked={suppressWarning}
              onChange={(e) => handleSuppressChange(e.target.checked)}
            >
              <Text size="xs" color="blackAlpha.600">
                Don't show this warning again
              </Text>
            </Checkbox>
            <Flex gap={3} mt={5}>
              <Button flex={1} variant="secondary" borderRadius={6} onClick={onClose}>
                Cancel
              </Button>
              <Button flex={1} variant="primary" borderRadius={6} onClick={confirmSwitch}>
                Switch
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    ),
    [isOpen, onClose, suppressWarning, confirmSwitch, handleSuppressChange],
  );

  return { handleSelection, WarningModal };
};
